import { useMemo, useState } from "react";
import s from "../clinic.module.css";
import { Button, Select } from "../../ui";
import {
  SCHEDULE_WEEKDAYS,
  findDraftTrainerSlotConflicts,
  findGroupTrainerSlotConflicts,
  formatDraftPreview,
  formatGroupTrainerConflictMessage,
  normalizeDurationMin,
  normalizeTimeInput,
  scheduleEntryId,
  sortScheduleEntries,
  sortWeekdayIds,
  splitScheduleEntry,
  trainerDisplayName,
  weekdayLabel,
} from "./scheduleStepUtils";
import ScheduleTimeDurationFields from "./ScheduleTimeDurationFields";

const QUICK_DAY_SETS = [
  { label: "Пн–Пт", ids: [1, 2, 3, 4, 5] },
  { label: "Пн, Ср, Пт", ids: [1, 3, 5] },
  { label: "Вых.", ids: [6, 0] },
];

export default function ScheduleStepForm({
  form,
  onChange,
  onSubmit,
  saving,
  submitLabel = "Продолжить",
  groupName,
  directionName,
  branchName = "",
  groups = [],
  trainers = [],
  activeGroupId,
  onSelectGroup,
  submitEnabled = false,
  pendingGroupNames = [],
  crossVenueConflicts = [],
  contextTrainerId = null,
  defaultTrainerId = null,
}) {
  const [addHint, setAddHint] = useState("");

  const resolvedDefaultTrainerId = defaultTrainerId || null;

  const entries = form.entries || [];
  const draftDays = form.draftDays || [];
  const startTime = normalizeTimeInput(form.start_time || "16:00");
  const durationMin = normalizeDurationMin(form.duration_min);

  const sortedEntries = useMemo(() => sortScheduleEntries(entries), [entries]);
  const daysWithSlots = useMemo(
    () => new Set(entries.map((e) => e.weekday)),
    [entries],
  );

  const trainerSlotConflicts = useMemo(
    () =>
      contextTrainerId
        ? findGroupTrainerSlotConflicts(entries, contextTrainerId, trainers)
        : [],
    [entries, contextTrainerId, trainers],
  );

  const conflictsByEntryId = useMemo(() => {
    const map = new Map();
    for (const c of trainerSlotConflicts) {
      if (!map.has(c.entryId)) map.set(c.entryId, []);
      map.get(c.entryId).push(c);
    }
    return map;
  }, [trainerSlotConflicts]);

  const draftTrainerConflicts = useMemo(
    () =>
      contextTrainerId
        ? findDraftTrainerSlotConflicts(
            draftDays,
            startTime,
            durationMin,
            entries,
            contextTrainerId,
            trainers,
          )
        : [],
    [
      draftDays,
      startTime,
      durationMin,
      entries,
      contextTrainerId,
      trainers,
    ],
  );

  const otherTrainerEntries = useMemo(() => {
    if (!contextTrainerId) return [];
    const ctx = String(contextTrainerId);
    return sortScheduleEntries(
      entries.filter((e) => {
        const tid = e.trainer_id ? String(e.trainer_id) : null;
        return tid && tid !== ctx;
      }),
    );
  }, [entries, contextTrainerId]);

  const ownTrainerEntries = useMemo(() => {
    if (!contextTrainerId) return sortedEntries;
    const ctx = String(contextTrainerId);
    return sortedEntries.filter(
      (e) => e.trainer_id && String(e.trainer_id) === ctx,
    );
  }, [sortedEntries, contextTrainerId]);

  const unassignedEntries = useMemo(() => {
    if (!contextTrainerId) return [];
    return sortScheduleEntries(entries.filter((e) => !e.trainer_id));
  }, [entries, contextTrainerId]);

  const draftPreview = useMemo(
    () => formatDraftPreview(draftDays, startTime, durationMin),
    [draftDays, startTime, durationMin],
  );

  const toggleDraftDay = (dayId) => {
    setAddHint("");
    onChange({
      ...form,
      draftDays: draftDays.includes(dayId)
        ? draftDays.filter((d) => d !== dayId)
        : [...draftDays, dayId],
    });
  };

  const applyQuickDays = (ids) => {
    setAddHint("");
    onChange({ ...form, draftDays: ids });
  };

  const addTime = () => {
    if (!draftDays.length) {
      setAddHint("Сначала выберите хотя бы один день");
      return;
    }

    const next = [...entries];
    const existing = new Set(
      next.map((e) => scheduleEntryId(e.weekday, e.start_time)),
    );
    let added = 0;

    for (const weekday of draftDays) {
      const id = scheduleEntryId(weekday, startTime);
      if (existing.has(id)) continue;
      existing.add(id);
      next.push({
        id,
        weekday,
        start_time: startTime,
        duration_min: durationMin,
        trainer_id:
          form.draft_trainer_id ||
          contextTrainerId ||
          resolvedDefaultTrainerId ||
          null,
      });
      added += 1;
    }

    if (!added) {
      setAddHint("Это время для выбранных дней уже в расписании");
      return;
    }

    onChange({
      ...form,
      entries: sortScheduleEntries(next),
      draftDays: [],
    });
    setAddHint("");
  };

  const removeEntry = (id) => {
    onChange({
      ...form,
      entries: entries.filter((e) => e.id !== id),
    });
  };

  const patchEntryTrainer = (id, trainerId) => {
    onChange({
      ...form,
      entries: entries.map((e) =>
        e.id === id ? { ...e, trainer_id: trainerId || null } : e,
      ),
    });
  };

  const trainerLabel = (trainerId) => {
    const effectiveId = trainerId || resolvedDefaultTrainerId;
    if (!effectiveId) return "Тренер не назначен";
    const t = trainers.find((x) => String(x.id) === String(effectiveId));
    return t?.full_name || t?.user?.full_name || "Тренер";
  };

  const canAddTime = draftDays.length > 0;

  const displayGroup = groupName?.trim() || "ваша группа";
  const displayDirection = directionName?.trim() || "";
  const displayBranch = branchName?.trim() || "";

  const activeConflicts = crossVenueConflicts.filter((c) =>
    c.groups.some((item) => item.groupName === displayGroup),
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (submitEnabled) onSubmit(e);
      }}
      className={[s.onboardingForm, s.onboardingScheduleForm].join(" ")}
    >
      {groups.length > 1 ? (
        <div className={s.onboardingScheduleGroupTabs} role="tablist" aria-label="Группы">
          {groups.map((g) => {
            const count = g.scheduleCount ?? 0;
            const active = g.id === activeGroupId;
            return (
              <button
                key={g.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={
                  active
                    ? s.onboardingScheduleGroupTabActive
                    : s.onboardingScheduleGroupTab
                }
                onClick={() => onSelectGroup?.(g.id)}
              >
                <span className={s.onboardingScheduleGroupTabText}>
                  <span className={s.onboardingScheduleGroupTabName}>
                    {g.name}
                  </span>
                  {g.branchName ? (
                    <span className={s.onboardingScheduleGroupTabVenue}>
                      {g.branchName}
                    </span>
                  ) : null}
                </span>
                {count > 0 ? (
                  <span className={s.onboardingScheduleGroupTabBadge}>{count}</span>
                ) : (
                  <span className={s.onboardingScheduleGroupTabPending}>нет</span>
                )}
              </button>
            );
          })}
        </div>
      ) : null}

      {pendingGroupNames.length > 0 ? (
        <p className={s.onboardingSchedulePendingHint} role="status">
          Осталось настроить:{" "}
          <strong>{pendingGroupNames.join(", ")}</strong>
        </p>
      ) : null}

      {crossVenueConflicts.length > 0 ? (
        <div className={s.onboardingScheduleConflictWarn} role="alert">
          <strong>Одинаковое время в разных залах</strong>
          <ul>
            {crossVenueConflicts.map((c) => (
              <li key={c.label}>{c.label}</li>
            ))}
          </ul>
          <span>
            При сохранении спросим подтверждение — проверьте, что это не ошибка.
          </span>
        </div>
      ) : null}

      {trainerSlotConflicts.length > 0 ? (
        <div className={s.onboardingScheduleConflictWarn} role="alert">
          <strong>Пересечение с другими тренерами группы</strong>
          <ul>
            {trainerSlotConflicts.map((c) => (
              <li key={`${c.entryId}-${c.otherSlotLabel}`}>
                {formatGroupTrainerConflictMessage(c)}
              </li>
            ))}
          </ul>
          <span>
            Можно сохранить, но лучше согласовать время с коллегой или выбрать другой слот.
          </span>
        </div>
      ) : null}

      <div className={s.onboardingScheduleGroupCard}>
        <span className={s.onboardingScheduleGroupLabel}>Расписание для</span>
        <p className={s.onboardingScheduleGroupName}>«{displayGroup}»</p>
        <div className={s.onboardingScheduleGroupMetaRow}>
          {displayDirection ? (
            <span className={s.onboardingScheduleGroupDirection}>
              {displayDirection}
            </span>
          ) : null}
          {displayBranch ? (
            <span className={s.onboardingScheduleGroupVenue}>
              📍 {displayBranch}
            </span>
          ) : null}
        </div>
        {activeConflicts.length > 0 ? (
          <p className={s.onboardingScheduleGroupConflictNote}>
            Это расписание совпадает с другой группой в другом зале — убедитесь,
            что время верное.
          </p>
        ) : null}
      </div>

      <section className={s.onboardingScheduleBlock}>
        <header className={s.onboardingScheduleBlockHead}>
          <span className={s.onboardingScheduleBlockNum}>1</span>
          <div>
            <h4 className={s.onboardingScheduleBlockTitle}>Дни недели</h4>
            <p className={s.onboardingScheduleBlockHint}>
              Нажмите день, чтобы выбрать. Повторный клик — снять.
            </p>
          </div>
        </header>

        <div className={s.onboardingScheduleQuickRow}>
          {QUICK_DAY_SETS.map((set) => (
            <button
              key={set.label}
              type="button"
              className={s.onboardingScheduleQuickBtn}
              onClick={() => applyQuickDays(set.ids)}
            >
              {set.label}
            </button>
          ))}
        </div>

        <div
          className={s.onboardingScheduleWeekdays}
          role="group"
          aria-label="Дни недели"
        >
          {SCHEDULE_WEEKDAYS.map((d) => {
            const isDraft = draftDays.includes(d.id);
            const hasSlot = daysWithSlots.has(d.id);
            const className = [
              s.onboardingScheduleDay,
              isDraft ? s.onboardingScheduleDayActive : "",
              !isDraft && hasSlot ? s.onboardingScheduleDayHasSlot : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <button
                key={d.id}
                type="button"
                className={className}
                onClick={() => toggleDraftDay(d.id)}
                aria-pressed={isDraft}
                title={
                  hasSlot && !isDraft ? "Уже есть занятие в этот день" : undefined
                }
              >
                {d.label}
                {hasSlot && !isDraft ? (
                  <span className={s.onboardingScheduleDayDot} aria-hidden />
                ) : null}
              </button>
            );
          })}
        </div>

        {draftDays.length > 0 ? (
          <p className={s.onboardingScheduleDraftDays}>
            Выбрано:{" "}
            <strong>
              {sortWeekdayIds(draftDays).map(weekdayLabel).join(", ")}
            </strong>
          </p>
        ) : null}
      </section>

      <section className={s.onboardingScheduleBlock}>
        <header className={s.onboardingScheduleBlockHead}>
          <span className={s.onboardingScheduleBlockNum}>2</span>
          <div>
            <h4 className={s.onboardingScheduleBlockTitle}>Время занятия</h4>
            <p className={s.onboardingScheduleBlockHint}>
              Укажите начало и длительность, затем добавьте в расписание.
            </p>
          </div>
        </header>

        <ScheduleTimeDurationFields
          startTime={startTime}
          durationMin={form.duration_min}
          onStartTimeChange={(next) =>
            onChange({
              ...form,
              start_time: normalizeTimeInput(next, startTime),
            })
          }
          onDurationChange={(next) =>
            onChange({
              ...form,
              duration_min: next,
            })
          }
        />

        {trainers.length > 0 && !contextTrainerId ? (
          <label className={s.onboardingScheduleTrainerPick}>
            <span className={s.onboardingScheduleTrainerPickLabel}>
              Тренер для новых слотов (необязательно)
            </span>
            <Select
              value={form.draft_trainer_id || resolvedDefaultTrainerId || ""}
              onChange={(e) =>
                onChange({
                  ...form,
                  draft_trainer_id: e.target.value || null,
                })
              }
            >
              {resolvedDefaultTrainerId ? null : (
                <option value="">Не указывать</option>
              )}
              {trainers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.full_name || t.user?.full_name}
                </option>
              ))}
            </Select>
          </label>
        ) : null}

        {draftPreview ? (
          <div className={s.onboardingSchedulePreview}>
            <span className={s.onboardingSchedulePreviewLabel}>Добавится</span>
            <span className={s.onboardingSchedulePreviewValue}>{draftPreview}</span>
          </div>
        ) : null}

        {draftTrainerConflicts.length > 0 ? (
          <div className={s.onboardingScheduleDraftConflict} role="alert">
            <strong>Конфликт по времени</strong>
            <ul>
              {draftTrainerConflicts.map((c) => (
                <li key={`${c.weekday}-${c.otherSlotLabel}`}>
                  {formatGroupTrainerConflictMessage(c)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <Button
          type="button"
          disabled={!canAddTime}
          onClick={addTime}
          className={s.onboardingScheduleAddBtn}
          block
        >
          + Добавить в расписание
        </Button>
        {addHint ? (
          <p className={s.onboardingScheduleAddHint} role="alert">
            {addHint}
          </p>
        ) : null}
      </section>

      <section className={s.onboardingScheduleSummary} aria-live="polite">
        <header className={s.onboardingScheduleSummaryHead}>
          <h4 className={s.onboardingScheduleSummaryTitle}>
            {contextTrainerId ? "Ваши слоты" : "Расписание группы"}
          </h4>
          {ownTrainerEntries.length > 0 ? (
            <span className={s.onboardingScheduleSummaryCount}>
              {ownTrainerEntries.length}{" "}
              {ownTrainerEntries.length === 1 ? "занятие" : "занятий"}
            </span>
          ) : null}
        </header>

        {otherTrainerEntries.length > 0 ? (
          <div className={s.onboardingScheduleOtherTrainers}>
            <p className={s.onboardingScheduleOtherTrainersTitle}>
              У группы уже занято другими тренерами
            </p>
            <ul className={s.onboardingScheduleOtherTrainersList}>
              {otherTrainerEntries.map((entry) => {
                const parts = splitScheduleEntry(entry);
                return (
                  <li key={entry.id} className={s.onboardingScheduleOtherTrainerItem}>
                    <span className={s.onboardingScheduleSummaryDay}>
                      {parts.day}
                    </span>
                    <div className={s.onboardingScheduleSummaryBody}>
                      <strong>
                        {parts.start} – {parts.end}
                      </strong>
                      <span className={s.onboardingScheduleSummaryTrainer}>
                        {trainerDisplayName(entry.trainer_id, trainers)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {unassignedEntries.length > 0 ? (
          <div className={s.onboardingScheduleOtherTrainers}>
            <p className={s.onboardingScheduleOtherTrainersTitle}>
              Занятия группы без назначенного тренера
            </p>
            <ul className={s.onboardingScheduleOtherTrainersList}>
              {unassignedEntries.map((entry) => {
                const parts = splitScheduleEntry(entry);
                return (
                  <li key={entry.id} className={s.onboardingScheduleOtherTrainerItem}>
                    <span className={s.onboardingScheduleSummaryDay}>
                      {parts.day}
                    </span>
                    <div className={s.onboardingScheduleSummaryBody}>
                      <strong>
                        {parts.start} – {parts.end}
                      </strong>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {ownTrainerEntries.length === 0 ? (
          <div className={s.onboardingScheduleSummaryEmpty}>
            <span className={s.onboardingScheduleSummaryEmptyIcon} aria-hidden>
              📅
            </span>
            <p>Пока пусто</p>
            <span>Выберите дни и время выше, затем нажмите «Добавить в расписание»</span>
          </div>
        ) : (
          <ul className={s.onboardingScheduleSummaryList}>
            {ownTrainerEntries.map((entry) => {
              const parts = splitScheduleEntry(entry);
              const entryConflicts = conflictsByEntryId.get(entry.id) || [];
              return (
                <li
                  key={entry.id}
                  className={[
                    s.onboardingScheduleSummaryCard,
                    entryConflicts.length
                      ? s.onboardingScheduleSummaryCardConflict
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <span className={s.onboardingScheduleSummaryDay}>
                    {parts.day}
                  </span>
                  <div className={s.onboardingScheduleSummaryBody}>
                    <strong>
                      {parts.start} – {parts.end}
                    </strong>
                    <span>{parts.duration} мин</span>
                    {trainers.length > 0 && !contextTrainerId ? (
                      <Select
                        value={
                          entry.trainer_id || resolvedDefaultTrainerId || ""
                        }
                        onChange={(e) =>
                          patchEntryTrainer(entry.id, e.target.value || null)
                        }
                        className={s.onboardingScheduleEntryTrainer}
                      >
                        {resolvedDefaultTrainerId ? null : (
                          <option value="">Тренер не назначен</option>
                        )}
                        {trainers.map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.full_name || t.user?.full_name}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <span className={s.onboardingScheduleSummaryTrainer}>
                        {trainerLabel(entry.trainer_id)}
                      </span>
                    )}
                    {entryConflicts.length > 0 ? (
                      <p className={s.onboardingScheduleEntryConflict}>
                        {formatGroupTrainerConflictMessage(entryConflicts[0])}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    className={s.onboardingScheduleSummaryRemove}
                    onClick={() => removeEntry(entry.id)}
                    aria-label={`Удалить ${parts.day} ${parts.start}`}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <div className={s.onboardingSheetFoot}>
        <Button
          type="submit"
          disabled={!submitEnabled || saving}
          className={s.onboardingSubmitBtn}
          block
        >
          {saving ? "Сохранение…" : submitLabel}
        </Button>
      </div>
    </form>
  );
}
